import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { resetPasswordExecute } from "../actions/registeredUserAction";
import MessageOverlay from "../components/MessageOverlay";
import { useHistory, useLocation } from "react-router-dom";

const RegResetPasswordExecuteScreen = () => {
  const location = useLocation();
  const code = new URLSearchParams(location.search).get("code");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [successful, setSuccessful] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showMessageOverlay, setShowMessageOverlay] = useState(false);
  const history = useHistory();

  const { register, formState: { errors }, watch } = useForm({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      dob: ""
    }
  });
  const password = watch('password');
  const passwordConfirm = watch('passwordConfirm');

  const registeredUserDetails = useSelector(
    (state) => state.registeredUserDetails
  );
  const { resetPasswordExecuteLoading, resetPasswordExecuteError, resetPasswordExecuteSuccess } = registeredUserDetails;
  const dispatch = useDispatch();

  // Password requirements checks
  const passwordChecks = [
    {
      label: "6-40 characters long",
      test: (pw) => pw.length >= 6 && pw.length <= 40,
    },
    {
      label: "At least one uppercase letter",
      test: (pw) => /[A-Z]/.test(pw),
    },
    {
      label: "At least one lowercase letter",
      test: (pw) => /[a-z]/.test(pw),
    },
    {
      label: "At least one number",
      test: (pw) => /\d/.test(pw),
    },
    {
      label: "At least one special character",
      test: (pw) => /[@$!%*?&]/.test(pw),
    },
  ];

  const handleMessageOverlayClose = () => {
    setShowMessageOverlay(false);
    history.replace("/registeredlogin");
  }

  useEffect(() => {
    if (password && passwordConfirm) {
      if (password !== passwordConfirm) {
        setPasswordMessage("Passwords do not match");
      } else {
        setPasswordMessage("");
      }
    }
  }, [password, passwordConfirm]);

  useEffect(() => {
    if (resetPasswordExecuteSuccess) {
      setShowMessageOverlay(true);
      // setCountdown(6);
      // const timer = setInterval(() => {
      //   setCountdown((prev) => prev - 1);
      // }, 1000);
      //
      // const redirectTimeout = setTimeout(() => {
      //   setShowMessageOverlay(false);
      //   history.replace("/registeredlogin");
      // }, 6000);
      //
      // return () => {
      //   clearInterval(timer);
      //   clearTimeout(redirectTimeout);
      // };
    }
  }, [resetPasswordExecuteSuccess, history]);

  const submitDebounceRef = useRef(0);

  const onSubmit = async () => {
    // Defensive: blur the currently focused element so validation or browser quirks
    // won't immediately re-focus the first invalid field (observed issue).
    try {
      if (typeof document !== 'undefined' && document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    } catch (e) {
      // ignore
    }
    // microtask fallback: ensure password input isn't left focused after any re-renders
    setTimeout(() => {
      try {
        const pw = typeof document !== 'undefined' && document.querySelector('input[name="password"]');
        if (pw && typeof pw.blur === 'function') pw.blur();
      } catch (e) {
        // ignore
      }
    }, 0);

    const now = Date.now();

    // Debounce to prevent multiple rapid submissions
    if (now - submitDebounceRef.current < 1000) {
      return; // Ignore if last submit was too recent
    }
    submitDebounceRef.current = now;

    setSuccessful(false);

    if (!password || !passwordConfirm) {
      setPasswordMessage("Please fill in all required fields.");
      return;
    }

    // Check password fields and abort if they don't match
    if (password !== passwordConfirm) {
      setPasswordMessage("Passwords do not match");
      return;
    }

    // Validate password against requirements
    const failedCheck = passwordChecks.find((check) => !check.test(password));
    if (failedCheck) {
      setPasswordMessage(`Password requirement not met: ${failedCheck.label}`);
      return;
    }

    // If we got here everything is good. Clear messages and dispatch.
    setPasswordMessage("");
    try {
      const result = await dispatch(resetPasswordExecute(password, code));
      setPasswordMessage(result.error);
    } catch (error) {
    }
  };

  // Password validation helper and button enablement
  const meetsPasswordChecks = (pw) => passwordChecks.every((c) => c.test(String(pw || "")));
  const canSubmit = !!password && !!passwordConfirm && password === passwordConfirm && meetsPasswordChecks(password);

  return (
    <Container fluid className="no-padding">
      <div className="card card-container backoffice-login-card-container">
        <h2 className="backoffice-title">Gift Card Claims Password Reset</h2>
        <MessageOverlay
          show={showMessageOverlay}
          handleClose={handleMessageOverlayClose}
          message={
            resetPasswordExecuteError
              ? resetPasswordExecuteError
              : (
                <>
                  Your password has been successfully changed. You may now log in with your updated password.<br />
                </>
              )
          }
          title={resetPasswordExecuteError ? "Error" : "Success"}
        />
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="gcotg-form" noValidate>
          {!successful && (
            <div>

              <div className="form-group gcotg-form-group">
                <label htmlFor="password">Choose a Password</label>
                <div style={{position: "relative"}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    style={{paddingRight: "2.5rem"}}
                    {...register('password', {
                      required: true,
                      minLength: 6,
                      maxLength: 40,
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
                    })}
                  />
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#888"
                    }}
                    tabIndex={-1}
                    role="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye}/>
                  </span>
                </div>
                <label htmlFor="password-confirm">Confirm Password</label>
                <div style={{position: "relative"}}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    style={{paddingRight: "2.5rem"}}
                    {...register('passwordConfirm', {
                      required: true,
                      minLength: 6,
                      maxLength: 40,
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/
                    })}
                  />
                  <span
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "0.75rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#888"
                    }}
                    tabIndex={-1}
                    role="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye}/>
                  </span>
                </div>
                <ul className="password-requirements">
                  {(password || passwordConfirm) && passwordChecks.map((req, idx) => {
                    const passed = req.test(password || "");
                    return (
                      <li key={idx} style={{color: passed ? "green" : "red", display: "flex", alignItems: "center"}}>
                        <FontAwesomeIcon
                          icon={passed ? faCheckCircle : faTimesCircle}
                          style={{marginRight: 8}}
                        />
                        {req.label}
                      </li>
                    );
                  })}
                </ul>
                {passwordMessage && (
                  <div className="form-group gcotg-error-message" role="alert">
                    {passwordMessage}
                  </div>
                )}
              </div>
              <div className="form-group">
                <button
                  type="submit"
                  className="btn btn-primary btn-block gcotg-button"
                  disabled={!canSubmit || resetPasswordExecuteLoading}
                >
                  Change Password
                </button>
              </div>
            </div>
          )}

        </form>
      </div>
    </Container>
  );
};

export default RegResetPasswordExecuteScreen;
